import { useEffect, useMemo } from 'react'
import { IListSettingsColumn } from '@cbgms/api'
import { usePersistedState } from '@cbgms/base/hooks/persisted-state/use-persisted-state'
import { Timing } from '@cbgms/base/utils/timing'
import { useDebouncedCallback } from 'use-debounce'

interface IUseListSearchHistoryParams {
  scope: string
  getUserID: () => string
}
export interface IColumnSearchHistoryItem {
  filters: IFilterRecord[]
  timestamp: number
}
type IFilterRecord = Pick<IListSettingsColumn, 'Name' | 'FilterOperator' | 'FilterValue'>
const maxHistoryRecords = 10

const isMatchingFilter = (newItem: IFilterRecord[], existingItem: IFilterRecord[]) => {
  if (newItem.length === 0) {
    return false
  }

  return newItem.every(c1 => {
    // check if there's a matching column
    const matchingColumn = existingItem.find(c2 => c2.Name === c1.Name)
    if (matchingColumn) {
      // check if the values also match
      return c1.FilterValue.startsWith(matchingColumn.FilterValue)
    }
    return false
  })
}

export const useListSearchHistory = ({ scope, getUserID }: IUseListSearchHistoryParams) => {
  const userID = getUserID()
  const [history, setHistory] = usePersistedState(`grid-search_${scope}_${userID}`, [] as IColumnSearchHistoryItem[])

  const onNewFilterSettings = useDebouncedCallback((settings: IListSettingsColumn[]) => {
    // store settings in history
    const newColumnFilters = settings.map(a => {
      return { FilterValue: a.FilterValue, FilterOperator: a.FilterOperator, Name: a.Name }
    })

    if (newColumnFilters.every(f => !f.FilterValue)) {
      // we can skip when every  filter is empty
      return
    }
    // try to find the column that could have an existing filter value.
    // check if the search is similar to an existing search.
    // remove/replace the old value

    const historyItemsToKeep = history.reduce<IColumnSearchHistoryItem[]>((outputRecords, record) => {
      if (outputRecords.length + 1 >= maxHistoryRecords) {
        // keep 1 less then the maximum, we'll always add the new item
        return outputRecords
      }
      const hasMatch = isMatchingFilter(newColumnFilters, record.filters)
      if (hasMatch) {
        // when the new filter matches an item in the list,  we skip it, we don't want duplicates
        return outputRecords
      }

      outputRecords.push(record)
      return outputRecords
    }, [])

    historyItemsToKeep.unshift({ filters: newColumnFilters, timestamp: Date.now() })
    setHistory(historyItemsToKeep)
  }, Timing.asyncOperationWait)

  useEffect(
    () => () => {
      // When the component is unmounted, we will flush the debounce to store any params.
      // there's some testing edge cases where the flush function could be null
      onNewFilterSettings.flush()
    },
    [onNewFilterSettings.flush]
  )

  return useMemo(() => ({ onNewFilterSettings, history }), [onNewFilterSettings, history])
}

export interface IWithUseListSearchProps {
  searchHistory: IColumnSearchHistoryItem[]
  storeNewSearchHistory: (input: IListSettingsColumn[]) => void
}
export const withUseListSearchHistory = (Component: any) => {
  return (props: any) => {
    const { history, onNewFilterSettings } = useListSearchHistory({ scope: props.scope, getUserID: props.getUserID })

    return <Component {...props} searchHistory={history} storeNewSearchHistory={onNewFilterSettings} />
  }
}
